/**
 * The operator's inactivity rule — GDD §14.
 *
 * PopiPlay sends it in `window.__OPTIONS__`:
 *
 *   license_rules: { log_out: { interval: 1200000 } }
 *
 * 1,200,000ms is twenty minutes, and it is a LICENSING requirement rather than
 * a feature: after that long with no player input the session has to end. It
 * was listed as unhandled in the GDD (§14 and §15 both) and nothing in the
 * engine read the field until now.
 *
 * ── Two things this module deliberately does NOT decide ──
 *
 * It does not decide the number — the operator does, per licence, and it can
 * differ per jurisdiction. A missing or nonsensical value turns the whole
 * feature off rather than falling back to a guess, because inventing a logout
 * interval is worse than not enforcing one.
 *
 * It does not decide what "activity" means either. See IdleLogoutManager: only
 * real player INPUT counts, never the game animating itself.
 */

/**
 * The error-popup code the timeout raises.
 *
 * A STRING, not a number, and that is on purpose: every other code in that
 * popup comes from the server and is numeric, so a synthetic number could
 * collide with one the backend adds later. `String(errorCode)` is what the
 * popup uses as its translation key, so this doubles as the i18n key.
 */
export const SESSION_TIMEOUT_CODE = "session_timeout";

/**
 * The operator's inactivity limit, in milliseconds.
 *
 * @returns {number} 0 when the option is absent or unusable, which means OFF.
 */
export const getIdleLogoutMs = () => {
  const raw = window.__OPTIONS__?.license_rules?.log_out?.interval;
  const ms = Number(raw);
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return ms;
};

/**
 * How far past the limit a session may be held ALIVE by game state before it is
 * worth saying so. Two whole intervals with no player input at all.
 *
 * See idleLogoutDecision's `stuck` flag: the flags below are all "keep alive",
 * so one of them stuck true does not break loudly — it silently disables the
 * operator's rule. That is the failure that matters, because a session which
 * ends early annoys a player while one that never ends fails an audit.
 */
export const IDLE_STUCK_FACTOR = 2;

/**
 * Should the session end now, and if not, what is holding it open?
 *
 * Pure, so the rule can be tested without a DOM, a store or a clock. The
 * manager owns the listeners and the polling; this owns the decision.
 *
 * ── What counts as ACTIVITY ──
 *
 * Player input, AND the game actually running — autoplay, a spin in flight, a
 * feature on screen, or a command on its way to the server. "If the game is
 * running and playing I'm not inactive" (Vladimir, 2026-09-10). A dormant
 * session is what the licence rule targets, not one mid-round.
 *
 * `playing` RESETS the clock rather than pausing it. Pausing would let the
 * limit expire underneath a long bonus and fire the instant it ended.
 *
 * ── What merely DEFERS ──
 *
 * `popupActive` and `criticalActive` do not reset anything. The player is gone;
 * we are only avoiding a popup stacked on a popup, or one thrown over a
 * half-finished animation. The check comes back on the next tick.
 *
 * @param   {object}  p
 * @param   {number}  p.now
 * @param   {number}  p.lastActivityAt
 * @param   {number}  p.intervalMs      0 disables the rule entirely.
 * @param   {boolean} [p.playing]
 * @param   {boolean} [p.popupActive]
 * @param   {boolean} [p.criticalActive]
 * @param   {boolean} [p.alreadyFired]
 * @returns {{ end: boolean, resetClock: boolean, stuck: boolean }}
 *          `resetClock` — the caller should push lastActivityAt to now.
 *          `stuck` — held alive well past the limit; worth logging, not acting on.
 */
export const idleLogoutDecision = ({
  now,
  lastActivityAt,
  intervalMs,
  playing = false,
  popupActive = false,
  criticalActive = false,
  alreadyFired = false,
} = {}) => {
  const idle = { end: false, resetClock: false, stuck: false };
  if (!intervalMs || alreadyFired) return idle;

  if (playing) {
    return {
      end: false,
      resetClock: true,
      stuck: now - lastActivityAt >= intervalMs * IDLE_STUCK_FACTOR,
    };
  }

  if (now - lastActivityAt < intervalMs) return idle;
  if (popupActive || criticalActive) return idle;

  return { end: true, resetClock: false, stuck: false };
};
